/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import React from 'react'

// Utils
import './src/styles/global.css'

// Styling
import CSSReset from './src/styles/reset'
import { ContextTemplate } from './src/context/AppContext'
// Setup
import {
  PrismicPreviewProvider,
  componentResolverFromMap,
} from 'gatsby-plugin-prismic-previews'
import { linkResolver } from './linkResolver'
import PageTemplate from './src/pages/{PrismicPage.url}'
import { PrismicProvider } from '@prismicio/react'
import { Link } from 'gatsby'

import './static/fonts/nexa-family/nexa.css'

/* eslint-disable react/prop-types */

/**
 * The preview runtime resolves each document's `url` with this resolver, and the
 * unpublished-preview flow matches that `url` against `location.pathname` with a
 * strict equality check. Since this site runs `trailingSlash: "always"`, Gatsby
 * navigates to `/foo/` while `linkResolver` returns `/foo` — so without this
 * normalisation an unpublished document never matches and the 404 page renders
 * instead of the page template. Build-time URLs are untouched; this only affects
 * the preview session. See `src/pages/404.tsx` for the other half.
 */
const previewLinkResolver = (doc) => {
  const url = linkResolver(doc)

  return url.endsWith('/') ? url : `${url}/`
}

/**
 * Styles do not need to be re-rendered and thus sit together with Providers
 * and other top-level components
 *
 * For more details, see: https://www.gatsbyjs.org/docs/browser-apis/#wrapRootElement
 */
export const wrapRootElement = ({ element }) => {
  return (
    <PrismicProvider
      internalLinkComponent={({ href, ...props }) => (
        <Link to={href} {...props} />
      )}
    >
      <PrismicPreviewProvider
        repositoryConfigs={[
          {
            linkResolver: previewLinkResolver,
            repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
            componentResolver: componentResolverFromMap({
              page: PageTemplate,
            }),
          },
        ]}
      >
        <CSSReset />
        <ContextTemplate>{element}</ContextTemplate>
      </PrismicPreviewProvider>
    </PrismicProvider>
  )
}

/**
 * Set up persistent UI elements that are not unmounted on page change
 *
 * For more details, see: https://www.gatsbyjs.org/docs/browser-apis/#wrapPageElement
 */
export const wrapPageElement = ({ element }) => <>{element}</>

/**
 * Leadfeeder: load ONLY after Cookiebot marketing consent
 */
const LEADFEEDER_SRC = 'https://sc.lfeeder.com/lftracker_v1_p1e024BvJGv4GB6d.js'

function loadLeadfeederOnce() {
  if (window.__leadfeeder_loaded) return
  window.__leadfeeder_loaded = true

  const s = document.createElement('script')
  s.async = true
  s.src = LEADFEEDER_SRC
  ;(document.head || document.body).appendChild(s)
}

function maybeLoadLeadfeeder() {
  if (window.Cookiebot?.consent?.marketing) loadLeadfeederOnce()
}

export const onInitialClientRender = () => {
  window.addEventListener('CookiebotOnLoad', maybeLoadLeadfeeder)
  window.addEventListener('CookiebotOnAccept', maybeLoadLeadfeeder)

  // covers the case Cookiebot is already initialized
  maybeLoadLeadfeeder()
}
